// Pure helpers behind the Devices screen (mockup 03): filtering, tag rules, address pools, live state.
import type { Device, DeviceStatus } from '../../api/types';
import { formatAgo, formatBytes } from '../../lib/format';
import { presence, type Presence } from '../../lib/status';

export interface DeviceFilter {
  query: string;
  /** Network id; null = all networks. */
  network: string | null;
  /** null = all tags. */
  tag: string | null;
  /** Keep only these ids ("Online only"); null = no such filter. */
  only?: ReadonlySet<string> | null;
}

/** Search matches name, DNS name, overlay address or a tag, case-insensitively; then network, tag and `only` narrow. */
export function filterDevices(devices: Device[], { query, network, tag, only = null }: DeviceFilter): Device[] {
  const q = query.trim().toLowerCase();
  return devices.filter(
    (d) =>
      (network === null || d.network === network) &&
      (tag === null || d.tags.includes(tag)) &&
      (only === null || only.has(d.id)) &&
      (!q ||
        d.name.toLowerCase().includes(q) ||
        (d.dnsName?.toLowerCase().includes(q) ?? false) ||
        d.overlayIp.includes(q) ||
        d.tags.some((t) => t.includes(q))),
  );
}

/** One row's live columns, from GET /devices/status. */
export interface StatusCells {
  presence: Presence;
  /** "42 s ago", "never"; an en dash before statuses load. */
  handshake: string;
  /** The handshake as an ISO time, for the tooltip; null when there is none. */
  handshakeAt: string | null;
  /** Bytes the DEVICE received / sent since the relay (re)added its peer: "1.2 GB". */
  rx: string;
  tx: string;
}

/**
 * The dot, Last handshake, Received and Sent of one device. `statuses` null = not loaded (or
 * failed): dashes. A device the relay never reported reads "never" and 0 B, as the API's nulls say.
 */
export function statusCells(
  deviceId: string,
  statuses: readonly DeviceStatus[] | null,
  live: ReadonlySet<string>,
  now: number,
): StatusCells {
  if (!statuses) return { presence: 'offline', handshake: '–', handshakeAt: null, rx: '–', tx: '–' };
  const status = statuses.find((x) => x.deviceId === deviceId);
  return {
    presence: presence(deviceId, status, live, now),
    handshake: formatAgo(status?.lastHandshakeAt, now),
    handshakeAt: status?.lastHandshakeAt ?? null,
    rx: formatBytes(status?.rx ?? 0),
    tx: formatBytes(status?.tx ?? 0),
  };
}

/** Every tag in use, sorted, once. */
export function allTags(devices: Device[]): string[] {
  return [...new Set(devices.flatMap((d) => d.tags))].sort();
}

/** Usable hosts in a /24: .1 to .254. */
export const POOL_HOSTS = 254;

export interface PoolUsage {
  id: string;
  cidr: string;
  used: number;
  free: number;
}

/** One entry per network: its members against the 254 usable hosts of its /24. */
export function poolUsage(devices: Device[], networks: { id: string; cidr: string }[]): PoolUsage[] {
  return networks.map(({ id, cidr }) => {
    const used = devices.filter((d) => d.network === id).length;
    return { id, cidr, used, free: Math.max(0, POOL_HOSTS - used) };
  });
}

/** CONTRACT 3.4 `PUT /devices/:id/tags`. */
export const TAG_RE = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;
export const MAX_TAGS = 16;

/** Why `tag` cannot be added to `existing`, or null when it can. */
export function validateTag(tag: string, existing: string[] = []): string | null {
  if (!tag) return 'Enter a tag.';
  if (!TAG_RE.test(tag)) {
    return 'Use up to 32 lowercase letters, digits or hyphens, starting and ending with a letter or digit.';
  }
  if (existing.includes(tag)) return `"${tag}" is already a tag.`;
  if (existing.length >= MAX_TAGS) return `A device has at most ${MAX_TAGS} tags.`;
  return null;
}

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

function ipToInt(ip: string): number | null {
  const m = IPV4.exec(ip);
  if (!m) return null;
  const octets = m.slice(1).map(Number);
  if (octets.some((o) => o > 255)) return null;
  return octets.reduce((n, o) => n * 256 + o, 0);
}

/** A host address of `cidr`: inside it, and neither its network nor its broadcast address. */
export function isInPool(ip: string, cidr: string): boolean {
  const [base, bits] = cidr.split('/');
  const n = ipToInt(ip.trim());
  const b = ipToInt(base ?? '');
  const size = 2 ** (32 - Number(bits));
  if (n === null || b === null || !Number.isFinite(size)) return false;
  const start = Math.floor(b / size) * size;
  return n > start && n < start + size - 1;
}

export type AddressHint = 'current' | 'free' | 'in use' | 'outside pool';

/** What the Address flyout says next to a typed address for `device`. */
export function addressHint(ip: string, device: Device, devices: Device[], cidr: string | null): AddressHint {
  const v = ip.trim();
  if (v === device.overlayIp) return 'current';
  if (!cidr || !isInPool(v, cidr)) return 'outside pool';
  return devices.some((d) => d.id !== device.id && d.overlayIp === v) ? 'in use' : 'free';
}
